import React, { useState } from 'react';
import useDGProfile from '../../hooks/useDGProfile';
import { auth } from '../../config/firebaseConfig';
import { API_BASE_URL } from '../../hooks/useChat/constants/apiConstants';

const STATE_LABELS = {
  timePref:       '시간 선호',
  // locationPref:   '장소 선호',  // 추후 논의 후 활성화
  // groupPref:      '그룹 선호',  // 추후 논의 후 활성화
  scheduleLoad:   '일정 밀도',
  fatigue:        '피로도',
  rejectPressure: '거절 압박',
  expandTrend:    '확장 성향',
  confidenceLevel:'확신도',
};

// 피로도·거절압박은 높을수록 빨간색, 나머지는 보라색
function barColor(key, value) {
  if (key === 'fatigue' || key === 'rejectPressure') {
    return value > 0.6 ? '#ef4444' : value > 0.3 ? '#f59e0b' : '#22c55e';
  }
  return '#7c3aed';
}

function DGProfilePanel({ userId }) {
  const { profile, stateVector, loading, error } = useDGProfile(userId);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  const handleSlowLearn = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/engine/dg/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      setRefreshMsg(data.ok ? '학습 완료' : '실패: ' + (data.error || ''));
    } catch (e) {
      setRefreshMsg('오류: ' + e.message);
    } finally {
      setRefreshing(false);
    }
  };

  if (!userId) return null;
  if (loading) return (
    <div style={styles.container}>
      <span style={styles.muted}>분석 중...</span>
    </div>
  );
  if (error || !profile) return (
    <div style={styles.container}>
      <div style={styles.title}>내 성향 분석</div>
      <div style={styles.muted}>분석 데이터를 불러올 수 없습니다</div>
    </div>
  );

  const totalEvents = profile.learning?.totalEvents || 0;
  const sv = profile.stateVector || {};
  const decisionStyle = profile.profile?.decisionStyle || {};
  const learning = profile.learning || {};

  const acceptRate = decisionStyle.acceptRate ?? 0.5;
  const styleLabel =
    acceptRate >= 0.7 ? '수락형' :
    acceptRate <= 0.3 ? '신중한 결정형' :
    '균형형';

  return (
    <div style={styles.container}>
      <div style={styles.title}>내 성향 분석</div>

      {/* 결정 스타일 */}
      <div style={styles.row}>
        <span style={styles.label}>결정 스타일</span>
        <span style={styles.value}>{styleLabel}</span>
      </div>

      {/* 상태 벡터 8개 전체 바 */}
      <div style={{ marginTop: 6 }}>
        {Object.entries(STATE_LABELS).map(([key, label]) => {
          const val = sv[key] ?? 0;
          const pct = Math.round(val * 100);
          return (
            <div key={key} style={styles.barRow}>
              <span style={styles.barLabel}>{label}</span>
              <div style={styles.barTrack}>
                <div style={{
                  ...styles.barFill,
                  width: `${pct}%`,
                  background: barColor(key, val),
                }} />
              </div>
              <span style={styles.pct}>{pct}%</span>
            </div>
          );
        })}
      </div>

      {/* 의사결정 스타일 상세 */}
      {decisionStyle.avgResponseTime !== undefined && (
        <div style={{ ...styles.muted, marginTop: 6 }}>
          평균 응답 {Math.round(decisionStyle.avgResponseTime)}초 ·
          수락률 {Math.round(acceptRate * 100)}%
          {decisionStyle.modifyBeforeAccept !== undefined &&
            ` · 수정후수락 ${Math.round(decisionStyle.modifyBeforeAccept * 100)}%`}
        </div>
      )}

      {/* 유행 추종 타입 */}
      {decisionStyle.trendFollowPoint !== undefined && (
        <div style={styles.row}>
          <span style={styles.label}>유행 수용</span>
          <span style={styles.value}>
            {decisionStyle.trendFollowPoint < 0.2
              ? '얼리어답터'
              : decisionStyle.trendFollowPoint < 0.5
              ? '초기 수용형'
              : '후기 수용형'}
          </span>
        </div>
      )}

      {/* 장소 태그 */}
      {(() => {
        const locPref = profile.profile?.locationPreferences || {};
        const topLocs = Object.entries(locPref)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .filter(([, v]) => v > 0);
        if (topLocs.length === 0) return null;
        return (
          <div style={{ marginTop: 4 }}>
            <span style={{ ...styles.muted, marginRight: 6 }}>자주 가는 곳</span>
            {topLocs.map(([loc]) => (
              <span key={loc} style={styles.tag}>{loc}</span>
            ))}
          </div>
        );
      })()}

      <div style={styles.muted}>
        학습 데이터 {learning.totalEdges || 0}개 ·
        {learning.lastLearnedAt
          ? ` 마지막 학습 ${new Date(learning.lastLearnedAt).toLocaleDateString('ko-KR')}`
          : ' 아직 장기학습 없음'}
      </div>

      {/* [테스트용] slow learning 수동 실행 버튼 */}
      <div style={{ marginTop: 8 }}>
        <button onClick={handleSlowLearn} disabled={refreshing} style={styles.refreshBtn}>
          {refreshing ? '학습 중...' : '학습 즉시 반영'}
        </button>
        {refreshMsg && <div style={{ ...styles.muted, marginTop: 4 }}>{refreshMsg}</div>}
      </div>

      {/* ML 전환 준비 */}
      {(() => {
        const MIN_EDGES = 500;
        const totalEdges = learning.totalEdges || 0;
        const mlReady = learning.mlReady || totalEdges >= MIN_EDGES;
        const remaining = Math.max(0, MIN_EDGES - totalEdges);
        const pct = Math.min(100, Math.round((totalEdges / MIN_EDGES) * 100));

        return (
          <div style={{ marginTop: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                AI 학습 상태
              </span>
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                color: mlReady ? '#16a34a' : '#7c3aed',
              }}>
                {mlReady ? '✓ AI 학습 완료' : `${remaining}개 더 필요`}
              </span>
            </div>
            <div style={{ height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${pct}%`,
                background: mlReady ? '#16a34a' : '#7c3aed',
                borderRadius: 2,
                transition: 'width 0.4s',
              }} />
            </div>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
              {totalEdges} / {MIN_EDGES}개
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const styles = {
  container: {
    padding: '12px 16px',
    background: '#f5f3ff',
    borderRadius: 12,
    margin: '8px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    color: '#7c3aed',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  label: {
    fontSize: 12,
    color: '#6b7280',
    minWidth: 72,
  },
  value: {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 3,
  },
  barLabel: {
    fontSize: 11,
    color: '#6b7280',
    width: 64,
    flexShrink: 0,
  },
  barTrack: {
    flex: 1,
    height: 6,
    background: '#e5e7eb',
    borderRadius: 3,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.4s',
  },
  pct: {
    fontSize: 10,
    color: '#9ca3af',
    width: 30,
    textAlign: 'right',
  },
  muted: {
    fontSize: 11,
    color: '#9ca3af',
  },
  refreshBtn: {
    fontSize: 12,
    padding: '5px 12px',
    borderRadius: 8,
    border: 'none',
    background: '#7c3aed',
    color: '#fff',
    cursor: 'pointer',
    opacity: 1,
  },
  tag: {
    display: 'inline-block',
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 10,
    background: '#ede9fe',
    color: '#7c3aed',
    marginRight: 4,
  },
};

export default DGProfilePanel;
